import { observer } from "mobx-react";
import React from "react";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { useStore } from "utils/useStore";
import AcxVirtualizedSelectMulti from "components/UI/Select/AcxVirtualizedSelectMulti";
import { IApplicationFilterComponent } from "../BaseFilterComponents/IApplicationFilterComponent";
import { useRestrictedOptions } from "../BaseFilterComponents/useRestrictedOptions";
import { usePruneSelectedValuesByOptions } from "../BaseFilterComponents/usePruneSelectedValues";

/**
 * Mirrors AgentsFilter, with one deliberate difference: managers are a flat list, so this uses the
 * ungrouped select. The Agent filter nests agents under their manager because an agent has one;
 * a manager has no parent to nest under.
 */
export const AgentManagersFilter = observer(function (
    props: IApplicationFilterComponent,
) {
    const filterValuesStore = useStore(ApplicationFilterValuesStore);
    const isAgentManagersLoading = filterValuesStore.getTaskLoading(
        ApplicationFilterValuesStore.Tasks.LOAD_AGENT_MANAGERS,
    );
    const { availableOptions: availableAgentManagers } = useRestrictedOptions({
        options: filterValuesStore.agentManagers,
        getOptionValue: (agentManager) => agentManager.id,
        optionRestriction: props.optionRestriction,
        restrictedValues: props.restrictedValues,
        excludeEmptyValues: true,
    });

    usePruneSelectedValuesByOptions({
        selectedValues: props.store.agentManagerIds,
        setSelectedValues: (agentManagerIds) =>
            props.store.setAgentManagerIds(agentManagerIds),
        availableOptions: availableAgentManagers,
        getSelectedValue: (agentManagerId) => agentManagerId,
        getOptionValue: (agentManager) => agentManager.id,
        enabled: !isAgentManagersLoading,
    });

    const agentManagerOptions = availableAgentManagers.map((agentManager) => ({
        ...agentManager,
        displayLabel: agentManager.fullName?.trim() || "Unknown Agent Manager",
    }));

    return (
        <AcxVirtualizedSelectMulti
            id={"agent-manager-filter-selector"}
            defaultSelectedIds={props.store.agentManagerIds}
            onApply={(agentManagerIds: string[]) => {
                props.store.setAgentManagerIds(agentManagerIds);
            }}
            options={agentManagerOptions}
            isLoading={isAgentManagersLoading}
            itemName="Agent Manager"
            labelField="displayLabel"
            showItemNameInSelectAll
        />
    );
});
